import React, {useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import AdminOrdersService from '../services/adminOrdersService';
import SectionHeader from '@/shared/components/SectionHeader';
import {dollars} from '@/shared/utils/money';

function actionError(e: unknown): string {
    return (e as {data?: {error?: string}})?.data?.error ?? (e as Error)?.message ?? 'Action failed.';
}

const AdminSubscriptionsView: React.FC = () => {
    const [page, setPage] = useState(1);
    const {data, isLoading, isError} = useQuery({
        queryKey: ['admin', 'subscriptions', page],
        queryFn: () => AdminOrdersService.subscriptions(page, 25, 'active'),
        staleTime: 30_000,
    });
    const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
    const qc = useQueryClient();
    const actMut = useMutation({
        mutationFn: (v: {id: number; action: 'cancel' | 'pause' | 'resume' | 'reschedule'; body: {reason?: string; next_test_due?: string}}) =>
            AdminOrdersService.subscriptionAction(v.id, v.action, v.body),
        onSuccess: () => {
            toast.success('Subscription updated.');
            qc.invalidateQueries({queryKey: ['admin', 'subscriptions']});
        },
        onError: (e) => toast.error(actionError(e)),
    });
    const onSubAction = (id: number, action: 'cancel' | 'pause' | 'resume' | 'reschedule') => {
        if (action === 'reschedule') {
            const d = window.prompt('New next-test-due date (YYYY-MM-DD):', '');
            if (d === null || d.trim() === '') return;
            actMut.mutate({id, action, body: {next_test_due: d.trim()}});
            return;
        }
        if (!window.confirm(`${action[0].toUpperCase() + action.slice(1)} this subscription?`)) return;
        actMut.mutate({id, action, body: {}});
    };

    return (
        <div className="p-6 space-y-4">
            <SectionHeader title="Subscriptions" />
            {isLoading && <p className="text-gray-500">Loading…</p>}
            {isError && <p className="text-mld-status-high">Could not load subscriptions.</p>}
            {data && (
                <>
                    <div className="overflow-x-auto bg-white rounded-2xl border border-mld-panel-border shadow-sm">
                        <table className="min-w-full text-sm">
                            <thead className="text-left text-gray-500 border-b border-mld-panel-border">
                                <tr>
                                    <th className="px-4 py-3">Customer</th>
                                    <th className="px-4 py-3">Program</th>
                                    <th className="px-4 py-3">Kind</th>
                                    <th className="px-4 py-3">Next due</th>
                                    <th className="px-4 py-3">Patient cost</th>
                                    <th className="px-4 py-3">Org</th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.subscriptions.map((s) => (
                                    <tr key={s.enrollment_id} className="border-b border-mld-panel-border">
                                        <td className="px-4 py-3 text-mld-text-primary">{s.customer_name ?? s.user_id}</td>
                                        <td className="px-4 py-3">{s.program}</td>
                                        <td className="px-4 py-3 text-gray-500">{s.enrollment_kind}</td>
                                        <td className="px-4 py-3">{s.next_test_due ?? '—'}</td>
                                        <td className="px-4 py-3">{dollars(s.patient_amount_cents)}</td>
                                        <td className="px-4 py-3 text-gray-500">{s.organization_name ?? s.organization_id ?? '—'}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {s.status === 'active' && (
                                                    <button type="button" onClick={() => onSubAction(s.enrollment_id, 'pause')}
                                                        className="rounded-md border border-gray-300 px-2 py-0.5 text-xs">Pause</button>
                                                )}
                                                {s.status === 'paused' && (
                                                    <button type="button" onClick={() => onSubAction(s.enrollment_id, 'resume')}
                                                        className="rounded-md border border-gray-300 px-2 py-0.5 text-xs">Resume</button>
                                                )}
                                                <button type="button" onClick={() => onSubAction(s.enrollment_id, 'reschedule')}
                                                    className="rounded-md border border-gray-300 px-2 py-0.5 text-xs">Reschedule</button>
                                                <button type="button" onClick={() => onSubAction(s.enrollment_id, 'cancel')}
                                                    className="rounded-md border border-gray-300 px-2 py-0.5 text-xs text-mld-status-high">Cancel</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {data.subscriptions.length === 0 && (
                                    <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No active subscriptions.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center justify-end gap-2 text-sm text-gray-500">
                        <button className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40"
                            disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
                        <span>Page {page} / {totalPages}</span>
                        <button className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40"
                            disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminSubscriptionsView;
